import {
  AlertTriangle,
  CalendarDays,
  FileText,
  Info,
  Truck,
  Wrench,
} from "lucide-react";
import { VehicleStatusBadge } from "@/components/vehicles/VehicleBadges";
import { useVehicle } from "@/hooks/useVehicles";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const vehiclePath = (id: string) => `/vehicles/${id}`;

function useVehicleItems({ id }: ContextParams): NavItem[] {
  const { data: vehicle } = useVehicle(id);
  const base = vehiclePath(id);
  const openDamages =
    vehicle?.damages?.filter((damage) => damage.status === "open").length ?? 0;
  return [
    {
      key: "information",
      label: "Information",
      href: base,
      exact: true,
      icon: Info,
    },
    {
      key: "bookings",
      label: "Bookings",
      href: `${base}/bookings`,
      icon: CalendarDays,
    },
    {
      key: "documents",
      label: "Documents",
      href: `${base}/documents`,
      icon: FileText,
    },
    { key: "service", label: "Service", href: `${base}/service`, icon: Wrench },
    {
      key: "damages",
      label: "Damages",
      href: `${base}/damages`,
      icon: AlertTriangle,
      badge: openDamages || null,
    },
  ];
}

function useVehicleHeader({ id }: ContextParams) {
  const { data: vehicle } = useVehicle(id);
  if (!vehicle) return {};
  const kicker = [
    vehicle.licensePlate,
    [vehicle.brand, vehicle.model].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" • ");
  return {
    kicker: kicker || null,
    title: vehicle.name,
    status: <VehicleStatusBadge status={vehicle.status} />,
  };
}

export const vehicleContext: NavContextDef = {
  type: "vehicle",
  typeLabel: "Vehicle",
  icon: Truck,
  match: (pathname) =>
    matchContextPath("/vehicles/:id", pathname, { id: ["planning", "new"] }),
  key: ({ id }) => `vehicle:${id}`,
  base: ({ id }) => vehiclePath(id),
  parent: () => ({ label: "Vehicles", href: "/vehicles" }),
  useHeader: useVehicleHeader,
  useItems: useVehicleItems,
};
